import React, { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'sonner';
import ProtectedRoutes from './ProtectedRoutes';
import { ErrorBoundary } from './components/ErrorBoundary';
import { PostHogPageviewTracker } from './providers/PostHogProvider';
import { PublicRouteDocumentMeta } from './components/seo/PublicRouteDocumentMeta';
import { PageLoader } from './components/ui/PageLoader';
import { ReferralCapture } from './components/Home/waitlist/ReferralCapture';

// Route-level code splitting (Epic H5): each page is its own chunk loaded on
// demand, so the initial bundle no longer ships every route up front.
const Home = lazy(() => import('./pages/Home'));
const Pricing = lazy(() => import('./pages/Pricing'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Stock = lazy(() => import('./pages/Stock'));
const StockAnalysis = lazy(() => import('./pages/StockAnalysis'));
const Blog = lazy(() => import('./pages/Blog'));
const Testimonials = lazy(() => import('./pages/Testimonials'));
const About = lazy(() => import('./pages/About'));
const Method = lazy(() => import('./pages/Method'));
const Tools = lazy(() => import('./pages/Tools'));
const ToolsFeeDrag = lazy(() => import('./pages/ToolsFeeDrag'));
const ForDiy = lazy(() => import('./pages/ForDiy'));
const OpenPortfolio = lazy(() => import('./pages/OpenPortfolio'));
const Proof = lazy(() => import('./pages/Proof'));
const PortfolioHealthPage = lazy(() => import('./health/PortfolioHealthPage'));
const Disclosures = lazy(() => import('./pages/Legal').then((m) => ({ default: m.Disclosures })));
const Privacy = lazy(() => import('./pages/Legal').then((m) => ({ default: m.Privacy })));
const Terms = lazy(() => import('./pages/Legal').then((m) => ({ default: m.Terms })));
const StartPlan = lazy(() => import('./start/StartPlan'));
const StartHoldings = lazy(() => import('./start/StartHoldings'));
const StartFlow = lazy(() => import('./start'));
const DropQuiz = lazy(() => import('./pages/DropQuiz'));
const VsRoboAdvisors = lazy(() => import('./pages/VsRoboAdvisors'));
const VsStockPickers = lazy(() => import('./pages/VsStockPickers'));
const NotFound = lazy(() => import('./pages/NotFound'));
const WaitlistUnsubscribePage = lazy(() => import('./pages/WaitlistUnsubscribePage'));

const RouteFallback = () => <PageLoader label="Loading page" />;

/** `/drop` is a short alias for the drop quiz; keeps `?ref=` and UTMs on the way. */
const DropQuizAlias = () => {
  const { search } = useLocation();
  return <Navigate to={{ pathname: '/quiz', search }} replace />;
};

const App = () => {
  return (
    <ErrorBoundary>
      <Router>
        <PublicRouteDocumentMeta />
        <ReferralCapture />
        <PostHogPageviewTracker />
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            {/* Public routes */}
            <Route path="/" element={<Home />} />
            <Route path="/from/:source" element={<Home />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/testimonials" element={<Testimonials />} />
            <Route path="/about" element={<About />} />
            <Route path="/method" element={<Method />} />
            <Route path="/tools" element={<Tools />} />
            <Route path="/tools/fee-drag" element={<ToolsFeeDrag />} />
            <Route path="/for/diy" element={<ForDiy />} />
            <Route path="/quiz" element={<DropQuiz />} />
            <Route path="/drop" element={<DropQuizAlias />} />
            <Route path="/open-portfolio" element={<OpenPortfolio />} />
            <Route path="/proof" element={<Proof />} />
            <Route path="/health" element={<PortfolioHealthPage />} />
            <Route path="/from/x" element={<PortfolioHealthPage />} />
            <Route path="/disclosures" element={<Disclosures />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/stock" element={<StockAnalysis />} />
            <Route path="/stock/:symbol" element={<Stock />} />
            <Route path="/start" element={<StartFlow />} />
            <Route path="/start/plan" element={<StartPlan />} />
            <Route path="/start/holdings" element={<StartHoldings />} />
            <Route path="/vs/robo-advisors" element={<VsRoboAdvisors />} />
            <Route path="/vs/stock-pickers" element={<VsStockPickers />} />
            <Route path="/waitlist/unsubscribe" element={<WaitlistUnsubscribePage />} />

            {/* Protected routes */}
            <Route element={<ProtectedRoutes />}>
              {/* The gate sends new accounts here; onboarding now happens in the public start flow. */}
              <Route path="/onboarding" element={<Navigate to="/start" replace />} />
              <Route path="/dashboard/*" element={<Dashboard />} />
              {/* Redirect old /saved route to dashboard saved portfolios */}
              <Route path="/saved" element={<Navigate to="/dashboard/saved-portfolios" replace />} />
            </Route>

            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </Router>
      <Toaster position="top-right" richColors />
    </ErrorBoundary>
  );
};

export default App;
